import { translateLabel } from "@/i18n/messages";
import React from "react";
import { ArrowSquareOutIcon, CalendarBlankIcon, CableCarIcon, CheckCircleIcon, ImageIcon, MapPinIcon } from "@phosphor-icons/react";
import { humanizeCanonicalValue } from "@/lib/destinationDetail";
import { destinationDecisionSnapshot, destinationMediaAssets, practicalInfoGroups } from "@/lib/destinationPresentation";
import { SuggestEdit } from "@/components/SuggestEdit";
import { SuggestTrail } from "@/components/SuggestTrail";
import { ReportContent } from "@/components/ReportContent";
import { DestinationEvents } from "@/components/DestinationEvents";

const isKnownValue = (value) => {
  if (value == null) return false;
  if (typeof value !== "string") return true;
  const normalized = value.trim().toLowerCase();
  return normalized !== "" && normalized !== "not available";
};

const uniqueValues = (...collections) => Array.from(new Set(
  collections
    .flatMap((collection) => (Array.isArray(collection) ? collection : []))
    .filter(isKnownValue),
));

export function DestinationMediaSection({ destination }) {
  const assets = destinationMediaAssets(destination);

  return (
    <section id="destination-media" data-detail-section="media" className="mb-12 scroll-mt-24">
      <SectionHeading title={translateLabel("Media")} />
      {assets.length === 0 ? (
        <div data-testid="destination-media-empty" className="flex min-h-52 items-center justify-center rounded-2xl border border-white/10 bg-gradient-to-br from-zinc-900 via-[#121214] to-black p-6 text-center">
          <div>
            <ImageIcon size={28} className="mx-auto text-[#CCFF00]" />
            <p className="mt-3 text-sm font-semibold text-zinc-300">{translateLabel("No verified destination media is available yet.")}</p>
            <p className="mt-1 text-xs text-zinc-500">{translateLabel("Bikeparks Finder does not hotlink third-party photos without usage rights.")}</p>
          </div>
        </div>
      ) : (
        <div data-testid="destination-media-gallery" className={`grid gap-3 ${assets.length > 1 ? "md:grid-cols-2" : ""}`}>
          {assets.map((asset, index) => (
            <div key={asset.src} className={`overflow-hidden rounded-2xl border border-white/10 bg-[#121214] ${index === 0 && assets.length > 2 ? "md:col-span-2" : ""}`}>
              <img
                src={asset.src}
                alt={`${destination.name} ${index === 0 ? "overview" : `media ${index + 1}`}`}
                className="h-64 w-full object-cover md:h-80"
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}


const readableDecisionValue = (label, value) => {
  const text = String(value || "").trim();
  if (!text || text === "Not available") return { headline: text || "Not available", details: [] };

  if (label === "Season") {
    const season = text.match(/^(?:(\d{4}) season:\s*)?([^.]*)\.?\s*(.*)$/i);
    if (season) {
      const [, year, range, rest] = season;
      return {
        headline: year ? `${range.trim()} · ${year}` : range.trim(),
        details: rest ? [rest.trim()] : [],
      };
    }
  }

  if (label === "Opening hours") {
    const colon = text.indexOf(":");
    const scheduleText = colon >= 0 ? text.slice(colon + 1) : text;
    const [schedule, ...restParts] = scheduleText.split(".").map((part) => part.trim()).filter(Boolean);
    const scheduleLines = (schedule || "")
      .split(";")
      .map((part) => part.trim())
      .filter(Boolean);
    return {
      headline: scheduleLines[0] || text,
      lines: scheduleLines.slice(1),
      details: restParts,
    };
  }

  return { headline: text, details: [] };
};

const DECISION_TONE_CLASSES = {
  good: "border-emerald-400/20 bg-emerald-400/[0.06] text-emerald-300",
  warn: "border-amber-400/20 bg-amber-400/[0.06] text-amber-300",
  alert: "border-red-400/20 bg-red-400/[0.06] text-red-300",
  neutral: "border-white/10 bg-white/[0.04] text-white",
  muted: "border-white/[0.07] bg-white/[0.025] text-zinc-400",
};

function DecisionCard({ icon: Icon, symbol, label, item, wide = false }) {
  const content = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-xl border border-white/[0.08] bg-black/20 text-[#CCFF00]">
          {Icon ? <Icon size={16} weight="bold" /> : <span className="text-sm font-black">{symbol}</span>}
        </span>
        {item.href && <ArrowSquareOutIcon size={13} className="text-zinc-600" />}
      </div>
      <div className="mt-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">{translateLabel(label)}</div>
        {(() => {
          const display = readableDecisionValue(label, item.value);
          return (
            <>
              <div className="mt-1 break-words text-sm font-extrabold leading-snug text-current">{translateLabel(display.headline)}</div>
              {display.lines?.map((line) => (
                <div key={line} className="mt-0.5 text-sm font-extrabold leading-snug text-current">{translateLabel(line)}</div>
              ))}
              {display.details?.map((detail) => (
                <div key={detail} className="mt-1.5 text-[11px] leading-relaxed text-zinc-500">{translateLabel(detail)}</div>
              ))}
            </>
          );
        })()}
        {item.detail && <div className="mt-1 text-[11px] leading-snug text-zinc-500">{translateLabel(item.detail)}</div>}
      </div>
    </>
  );

  const className = `min-w-0 rounded-xl border p-3 transition-colors ${DECISION_TONE_CLASSES[item.tone] || DECISION_TONE_CLASSES.muted} ${wide ? "col-span-2" : ""}`;

  return item.href ? (
    <a href={item.href} target="_blank" rel="noreferrer" className={className}>
      {content}
    </a>
  ) : (
    <div className={className}>{content}</div>
  );
}

export function DestinationVisitDecisionPanel({
  destination,
  accessLabel,
  directionsUrl,
}) {
  const snapshot = destinationDecisionSnapshot(destination, { accessLabel });
  const statusTone = snapshot.status.tone;
  const statusDot = {
    good: "bg-emerald-400",
    alert: "bg-red-400",
    warn: "bg-amber-400",
    neutral: "bg-[#CCFF00]",
    muted: "bg-zinc-600",
  }[statusTone] || "bg-zinc-600";

  return (
    <div data-testid="destination-visit-decision-panel">
      <a
        href={snapshot.status.href || undefined}
        target={snapshot.status.href ? "_blank" : undefined}
        rel={snapshot.status.href ? "noreferrer" : undefined}
        className={`mb-3 flex items-center justify-between gap-3 rounded-xl border px-3.5 py-3 ${
          snapshot.status.href
            ? "border-[#CCFF00]/20 bg-[#CCFF00]/[0.05] hover:bg-[#CCFF00]/[0.08]"
            : "border-white/[0.08] bg-black/15"
        }`}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${statusDot}`} />
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">{translateLabel("Trail status")}</div>
            <div className="truncate text-sm font-extrabold text-white">{translateLabel(snapshot.status.value)}</div>
          </div>
        </div>
        {snapshot.status.href && (
          <span className="flex shrink-0 items-center gap-1 text-[11px] font-bold text-[#CCFF00]">
            {translateLabel("Official")} <ArrowSquareOutIcon size={13} />
          </span>
        )}
      </a>

      <div className="grid grid-cols-2 gap-2">
        <DecisionCard icon={CheckCircleIcon} label={translateLabel("Legal")} item={snapshot.legal} />
        <DecisionCard icon={CableCarIcon} label={translateLabel("Uplift")} item={snapshot.uplift} />
        <DecisionCard symbol="⚡" label={translateLabel("E-bike")} item={snapshot.ebike} />
        <DecisionCard icon={CalendarBlankIcon} label={translateLabel("Season")} item={snapshot.season} />
        <DecisionCard symbol="◷" label={translateLabel("Opening hours")} item={snapshot.hours} wide />
        <DecisionCard symbol="€" label={translateLabel("Day ticket")} item={snapshot.price} wide />
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {directionsUrl && (
          <a
            href={directionsUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-2 text-[11px] font-bold text-zinc-300 hover:border-white/20 hover:text-white"
          >
            <MapPinIcon size={14} className="text-[#CCFF00]" /> {translateLabel("Entry point")}
          </a>
        )}
        {snapshot.price.href && (
          <a
            href={snapshot.price.href}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-2 text-[11px] font-bold text-zinc-300 hover:border-white/20 hover:text-white"
          >
            € {translateLabel("Official prices")} <ArrowSquareOutIcon size={12} />
          </a>
        )}
      </div>

      {snapshot.verifiedAt && (
        <div className="mt-3 flex items-center gap-1.5 text-[10px] font-semibold text-zinc-600">
          <CheckCircleIcon size={12} /> {translateLabel("Visit info checked")} {snapshot.verifiedAt}
        </div>
      )}
    </div>
  );
}

export function DestinationPracticalInfo({
  destination,
  accessPointLabel,
  accessLabel,
  directionsUrl,
  compact = false,
}) {
  const compactHiddenLabels = new Set([
    "Legal access",
    "Uphill access",
    "Pedal access",
    "Bike transport",
    "E-bike transport",
    "Season",
    "Day ticket",
    "Official prices",
    "Trail status",
    "Visit info verified",
  ]);
  const visibleGroups = practicalInfoGroups(destination, { accessPointLabel, accessLabel })
    .map((group) => ({
      ...group,
      items: group.items
        .filter((item) => item.label !== "Website")
        .filter((item) => !compact || !compactHiddenLabels.has(item.label))
        .map((item) => (
          item.label === "Primary access point" && directionsUrl
            ? { ...item, value: "Open access point in Maps", href: directionsUrl }
            : item
        ))
        .filter((item) => isKnownValue(item.value)),
    }))
    .filter((group) => group.items.length > 0);
  const featureValues = uniqueValues(destination.facilities, destination.attributes);

  if (visibleGroups.length === 0 && featureValues.length === 0) return null;

  return (
    <section
      id="practical-info"
      data-detail-section="practical-info"
      className={compact ? "" : "mb-12"}
      data-testid="destination-practical-info"
    >
      {!compact && <SectionHeading title={translateLabel("Practical Info")} subtitle={translateLabel("Planning details for your visit.")} />}
      <div className={compact ? "space-y-3" : "grid gap-4 md:grid-cols-2"}>
        {visibleGroups.map((group) => (
          <div
            key={group.id}
            data-testid={`practical-group-${group.id}`}
            className={`border border-white/10 bg-[#121214] ${compact ? "rounded-xl p-4" : "rounded-2xl p-5"}`}
          >
            <h3 className={`font-display font-bold uppercase text-white ${compact ? "text-base" : "text-lg"}`}>
              {translateLabel(group.title)}
            </h3>
            <dl className={compact ? "mt-3 space-y-3" : "mt-4 space-y-4"}>
              {group.items.map((item) => (
                <div key={translateLabel(item.label)}>
                  <dt className="label-tiny">{translateLabel(item.label)}</dt>
                  <dd className="mt-1 break-words text-sm font-semibold text-zinc-200">
                    {item.href ? (
                      <a href={item.href} target="_blank" rel="noreferrer" className="text-[#CCFF00] underline-offset-4 hover:underline">
                        {translateLabel(item.value)}
                      </a>
                    ) : translateLabel(item.value)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>

      {featureValues.length > 0 && (
        <TagGroup
          title={translateLabel("What you'll find")}
          values={featureValues}
          humanize
          compact={compact}
        />
      )}
    </section>
  );
}

export function DestinationCommunityActions({ destination, compact = false, includeEvents = true }) {
  return (
    <>
      {includeEvents && <DestinationEvents destinationId={destination.id} />}
      <section
        id="community-actions"
        data-detail-section="community-actions"
        className={`scroll-mt-28 rounded-2xl border border-white/10 bg-[#121214] ${compact ? "p-4" : "mb-12 p-5"}`}
        data-testid="destination-community-actions"
      >
        <div className="label-tiny text-[#CCFF00]">{translateLabel("Community")}</div>
        <h2 className={`mt-1 font-display font-bold uppercase text-white ${compact ? "text-xl" : "text-2xl"}`}>
          {translateLabel("Help improve this destination")}
        </h2>
        <p className="mt-1 text-sm leading-relaxed text-zinc-400">
          {translateLabel("Spotted outdated information, a missing trail or another problem? Help other riders by letting us know.")}
        </p>
        <div className="mt-4 space-y-3">
          <SuggestEdit
            targetType="DESTINATION"
            targetId={destination.id}
            targetName={destination.name || translateLabel("this destination")}
            compact
          />
          <SuggestTrail
            destinationId={destination.id}
            destinationName={destination.name || "this destination"}
            compact
          />
          <ReportContent
            targetType="DESTINATION"
            targetId={destination.id}
            targetName={destination.name || "this destination"}
            compact
          />
        </div>
      </section>
    </>
  );
}

function TagGroup({ title, values = [], humanize = false, compact = false }) {
  if (values.length === 0) return null;

  return (
    <div className={`border border-white/10 bg-[#121214] ${compact ? "mt-3 rounded-xl p-4" : "mt-4 rounded-2xl p-5"}`}>
      <div className="label-tiny mb-3">{title}</div>
      <div className="flex flex-wrap gap-2">
        {values.map((value) => (
          <span key={value} className="rounded-full bg-white/5 px-3 py-1.5 text-xs font-semibold text-zinc-300">
            {translateLabel(humanize ? humanizeCanonicalValue(value) : value)}
          </span>
        ))}
      </div>
    </div>
  );
}

function SectionHeading({ title, subtitle }) {
  return (
    <div className="mb-4">
      <h2 className="font-display text-2xl font-bold uppercase text-white sm:text-3xl">{title}</h2>
      {subtitle && <p className="mt-1 text-sm text-zinc-400">{subtitle}</p>}
    </div>
  );
}
